import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ISaveAreTipsHiddenRequest,
  ISaveCurrentUserInfoRequest,
  ISaveIsMachineBarPinnedRequest,
  ISaveIsStartBannerHiddenRequest,
  ISaveLanguageCodeRequest,
  ISaveThemeRequest,
  ISaveTimezoneRequest
} from '@treneo/models';
import { normalizeLanguageCode } from '@treneo/shared/constants';

import { usersApi } from './api';
import { CURRENT_USER_QUERY_KEY } from './current-user-query';

export const useSaveLanguage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ languageCode, isLogged }: { languageCode: string; isLogged: boolean }) => {
      if (isLogged) {
        await usersApi.saveLanguageCode({
          languageCode: normalizeLanguageCode(languageCode)
        } as ISaveLanguageCodeRequest);
      }
      return languageCode;
    },
    onSuccess: (_, { isLogged }) => {
      if (isLogged) {
        void queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      }
    }
  });
};

export const useSaveTheme = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ theme, isLogged }: { theme: string; isLogged: boolean }) => {
      if (isLogged) {
        await usersApi.saveTheme({ theme } as ISaveThemeRequest);
      }
      return theme;
    },
    onSuccess: (_, { isLogged }) => {
      if (isLogged) {
        void queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      }
    }
  });
};

export const useSaveTimezone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ timezone, isLogged }: { timezone: string; isLogged: boolean }) => {
      if (isLogged) {
        await usersApi.saveTimezone({ timezone } as ISaveTimezoneRequest);
      }
      return timezone;
    },
    onSuccess: (_, { isLogged }) => {
      if (isLogged) {
        void queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      }
    }
  });
};

export const useSaveIsStartBannerHidden = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      isStartBannerHidden,
      isLogged
    }: {
      isStartBannerHidden: boolean;
      isLogged: boolean;
    }) => {
      if (isLogged) {
        await usersApi.saveIsStartBannerHidden({
          isStartBannerHidden
        } as ISaveIsStartBannerHiddenRequest);
      }
      return isStartBannerHidden;
    },
    onSuccess: (_, { isLogged }) => {
      if (isLogged) {
        void queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      }
    }
  });
};

export const useSaveIsMachineBarPinned = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      isMachineBarPinned,
      isLogged
    }: {
      isMachineBarPinned: boolean;
      isLogged: boolean;
    }) => {
      if (isLogged) {
        await usersApi.saveIsMachineBarPinned({
          isMachineBarPinned
        } as ISaveIsMachineBarPinnedRequest);
      }
      return isMachineBarPinned;
    },
    onSuccess: (_, { isLogged }) => {
      if (isLogged) {
        void queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      }
    }
  });
};

export const useSaveAreTipsHidden = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      areTipsHidden,
      isLogged
    }: {
      areTipsHidden: boolean;
      isLogged: boolean;
    }) => {
      if (isLogged) {
        await usersApi.saveAreTipsHidden({
          areTipsHidden
        } as ISaveAreTipsHiddenRequest);
      }
      return areTipsHidden;
    },
    onSuccess: (_, { isLogged }) => {
      if (isLogged) {
        void queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      }
    }
  });
};

export const useSaveCurrentUserInfo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: ISaveCurrentUserInfoRequest) => {
      await usersApi.saveCurrentUserInfo(request);
      const result = await usersApi.getCurrentUser();
      if (!result.isSuccess || !result.data) {
        throw new Error(result.error ?? 'Get current user info after save failed');
      }
      return result.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, data);
    }
  });
};
